import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { applyOpacityToHex } from '../../../../helpers/applyOpacityToHex';

import { useOverlayState } from '../../../../hooks/useOverlayState';
import { useResponsiveBreakpoints } from '../../../../hooks/useResponsiveBreakpoints';

import Button from '../../../../design-system/Button/Button';
import RequestPlaybookModal from '../RequestPlaybookModal/RequestPlaybookModal';
import PlaybookTemplateMoreOptionsPopup from '../../PlaybookTemplateMoreOptionsPopup/PlaybookTemplateMoreOptionsPopup';
import { ButtonIcon } from '../../../../design-system';
import { ArrowGoBackLineIcon, More2FillIcon } from '../../../../design-system/Icons';

const PlaybookTemplateHeader = ({
    id,
    name,
    description,
    icon_color,
    is_live,
    backLinkHref,
    isEditable,
    setPlaybookTemplate,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { isMobile } = useResponsiveBreakpoints();

    const { isOpened: isPopupOpened, open: openPopup, close: closePopup } = useOverlayState();
    const { isOpened: isModalOpened, open: openModal, close: closeModal } = useOverlayState();

    const onIsLiveChange = (data) => {
        setPlaybookTemplate((template) => ({ ...template, is_live: data.is_live }));
    };

    const onDelete = () => navigate(backLinkHref, { replace: true });

    const text = isMobile ? 'Back' : 'Back to Library';

    return (
        <>
            <div
                className="px-4 py-5 xs:px-5 md:px-8 md:py-7 lg:px-[40px] xl:py-[40px] xl:px-[60px] flex flex-col lg:gap-4 xl:gap-6"
                style={{ backgroundColor: applyOpacityToHex(icon_color, 20) }}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col items-start min-[380px]:flex-row min-[380px]:items-center gap-x-5 gap-y-0.5">
                        <Button
                            type="link"
                            size="xs"
                            text={text}
                            leadingIcon={ArrowGoBackLineIcon}
                            onClick={() => navigate(backLinkHref, { state: { from: location } })}
                        />
                        <p className="font-body text-body-regular-s text-neutral-300 hidden md:block">
                            Playbooks /
                        </p>
                    </div>

                    <div className="relative">
                        {!isEditable && (
                            <Button
                                type="primary"
                                size="sm"
                                text="Request Playbook"
                                onClick={openModal}
                            />
                        )}

                        {isEditable && (
                            <>
                                <ButtonIcon
                                    type="link"
                                    size="sm"
                                    icon={More2FillIcon}
                                    onClick={openPopup}
                                />

                                {isPopupOpened && (
                                    <PlaybookTemplateMoreOptionsPopup
                                        id={id}
                                        is_live={is_live}
                                        onIsLiveChange={onIsLiveChange}
                                        onDelete={onDelete}
                                        position="top-[42px] right-0"
                                        onClose={closePopup}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="pt-5 md:pt-7 lg:pt-[40px]">
                    <h1 className="font-heading-bold text-heading-bold-l text-black mb-6">
                        {name}
                    </h1>
                    <p className="font-body text-body-regular-m text-neutral-300 whitespace-pre-wrap">
                        {description}
                    </p>
                </div>
            </div>

            {isModalOpened && (
                <RequestPlaybookModal
                    templateId={id}
                    templateName={name}
                    templateDescription={description}
                    onClose={closeModal}
                />
            )}
        </>
    );
};

export default PlaybookTemplateHeader;
